import { envConfig } from "../../utils/constants";
import {
  AddBundleItemState,
  AddVirtualItemState,
  BillingAddressState,
  CartState,
  CreateCartState,
  RemoveAllCartItemVariableState,
  RemoveCartItemResponseState,
  RemoveCartItemVariableState,
} from "../../utils/interfaces/cart";
import { client } from "../graphql";
import {
  ADD_BUNDLE_ITEMS_TO_CART,
  ADD_VIRTUAL_ITEMS_TO_CART,
  APPLY_COUPON_TO_CART,
  CREATE_EMPTY_CART,
  REMOVE_ALL_ITEMS_FROM_CART,
  REMOVE_CART_ITEM,
  REMOVE_COUPON_CODE,
  SET_BILLING_ADDRESS_ON_CART,
  SET_EMAIL_ON_CART,
} from "./mutations";
import { CART_DETAILS_QUERY, CHECK_IS_USER_AUTH } from "./queries";

class CartServices {
  async fetchCartData(cart_id: string): Promise<CartState> {
    const res = await client.query({
      query: CART_DETAILS_QUERY,
      variables: { cart_id },
      context: { headers: { store: envConfig.store } },
      fetchPolicy: "no-cache",
    });
    return res.data.cart;
  }

  async createEmptyCart(): Promise<CreateCartState> {
    const res = await client.mutate({
      mutation: CREATE_EMPTY_CART,
      context: { headers: { store: envConfig.store } },
    });
    return res.data;
  }

  async addBundleItemsToCart(
    variables: AddBundleItemState
  ): Promise<CartState> {
    await client.mutate({
      mutation: ADD_BUNDLE_ITEMS_TO_CART,
      variables,
      context: { headers: { store: envConfig.store } },
    });
    return this.fetchCartData(variables.cartId);
  }

  async addVirtualItemsToCart(
    variables: AddVirtualItemState
  ): Promise<CartState> {
    await client.mutate({
      mutation: ADD_VIRTUAL_ITEMS_TO_CART,
      variables,
      context: { headers: { store: envConfig.store } },
    });
    return this.fetchCartData(variables.cartId);
  }

  async setBillingAddressOnCart(
    variables: BillingAddressState
  ): Promise<CartState> {
    await client.mutate({
      mutation: SET_BILLING_ADDRESS_ON_CART,
      variables,
      context: { headers: { store: envConfig.store } },
    });
    return this.fetchCartData(variables.cartId);
  }

  async setEmailOnCart(variables: {
    cart_id: string;
    email: string;
  }): Promise<CartState> {
    await client.mutate({
      mutation: SET_EMAIL_ON_CART,
      variables,
      context: { headers: { store: envConfig.store } },
    });
    return this.fetchCartData(variables.cart_id);
  }

  async removeCartItems(
    variables: RemoveCartItemVariableState
  ): Promise<RemoveCartItemResponseState> {
    const res = await client.mutate({
      mutation: REMOVE_CART_ITEM,
      variables,
      context: { headers: { store: envConfig.store } },
    });
    return res.data.removeItemFromCart.cart;
  }

  checkisUserAuth = async (cartId: string): Promise<string> => {
    const res = await client.query({
      query: CHECK_IS_USER_AUTH,
      variables: { cartId },
      context: { headers: { store: envConfig.store } },
    });
    if (res.data.cart && res.data.cart.id) return res.data.cart.id;
    else return "";
  };

  async removeAllCartItems(
    variables: RemoveAllCartItemVariableState
  ): Promise<RemoveCartItemResponseState> {
    const res = await client.mutate({
      mutation: REMOVE_ALL_ITEMS_FROM_CART,
      variables,
      context: { headers: { store: envConfig.store } },
    });
    return res.data.removeAllItemFromCart.cart;
  }

  async applyCouponCode(variables: {
    cart_id: string;
    couponCode: string;
  }): Promise<CartState> {
    await client.mutate({
      mutation: APPLY_COUPON_TO_CART,
      variables,
      context: { headers: { store: envConfig.store } },
    });
    return this.fetchCartData(variables.cart_id);
  }

  async removeCouponCode(variables: { cart_id: string }): Promise<CartState> {
    await client.mutate({
      mutation: REMOVE_COUPON_CODE,
      variables,
      context: { headers: { store: envConfig.store } },
    });
    return this.fetchCartData(variables.cart_id);
  }

  setCartData = async (
    cart_id: string,
    action: (res: any) => void,
    setLoading: (res: any) => void
  ) => {
    await this.fetchCartData(cart_id)
      .then((res: any) => {
        // need to check
        if (res) action(res);
        setLoading(false);
      })
      .catch((e: Error) => {
        setLoading(false);
        return e;
      });
  };
}

export default new CartServices();

export const setCartId = (id: string) => {
  localStorage.setItem(`${envConfig.store}_${envConfig.env}_cartId`, id);
};

export const getCartId = () => {
  return localStorage.getItem(`${envConfig.store}_${envConfig.env}_cartId`);
};

export const setOrderId = (id: string) => {
  localStorage.setItem(`${envConfig.store}_${envConfig.env}_orderId`, id);
};
export const getOrderId = () => {
  return localStorage.getItem(`${envConfig.store}_${envConfig.env}_orderId`);
};
